import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Signal, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, distinctUntilChanged, finalize, map, of, pairwise, switchMap, timer } from 'rxjs';

import { LoadingSpinnerComponent } from '@shared/components/loading-spinner';
import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

const OPEN_DELAY_MS = 200;

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  readonly #overlay = inject(Overlay);
  private overlayRef?: OverlayRef;

  private readonly subscriptionRecordSubject$ = new BehaviorSubject<Record<string, boolean>>({});

  public constructor() {
    this.handleVisibility();
  }

  public addListener(listener$: Observable<boolean | boolean[] | undefined> | Signal<boolean | boolean[] | undefined>): void {
    if (!(listener$ instanceof Observable)) {
      listener$ = toObservable(listener$);
    }

    const uuid = `listener-${getRuntimeUniqueId()}`;

    listener$
      .pipe(
        finalize(() => {
          this.clearListener(uuid);
        })
      )
      .subscribe({
        next: (val) => {
          if ((Array.isArray(val) && val.some(Boolean)) || (typeof val === 'boolean' && val)) {
            this.setListenerTrue(uuid);
          } else {
            this.setListenerFalse(uuid);
          }
        },
      });
  }

  private setListenerTrue(uuid: string): void {
    this.subscriptionRecordSubject$.next({ ...this.subscriptionRecordSubject$.value, [uuid]: true });
  }

  private setListenerFalse(uuid: string): void {
    this.subscriptionRecordSubject$.next({ ...this.subscriptionRecordSubject$.value, [uuid]: false });
  }

  private clearListener(uuid: string): void {
    const record = { ...this.subscriptionRecordSubject$.getValue() };
    delete record[uuid];
    this.subscriptionRecordSubject$.next(record);
  }

  private handleVisibility(): void {
    this.subscriptionRecordSubject$
      .asObservable()
      .pipe(
        map((record) => Object.values(record).some(Boolean)),
        distinctUntilChanged(),
        pairwise(),
        switchMap(([prev, curr]) => {
          return !prev && curr ? timer(OPEN_DELAY_MS).pipe(map(() => true)) : of(curr);
        }),
        distinctUntilChanged()
      )
      .subscribe((isVisible) => {
        if (isVisible) {
          if (this.overlayRef) return;
          this.overlayRef = this.#overlay.create({
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
            positionStrategy: this.#overlay.position().global().centerHorizontally().centerVertically(),
            scrollStrategy: this.#overlay.scrollStrategies.block(),
          });
          const componentPortal = new ComponentPortal(LoadingSpinnerComponent);
          this.overlayRef.attach(componentPortal);
        } else {
          if (!this.overlayRef) return;
          this.overlayRef?.dispose();
          delete this.overlayRef;
        }
      });
  }
}
